/**
 * <bp-lang-dropdown> — click-to-open language popover used in the navbar.
 *
 * Server-rendered markup (shared/_lang_dropdown.html.erb) already produces
 * the trigger button + grid menu. This element upgrades that markup with:
 *   - click-to-toggle with aria-expanded
 *   - click-outside and Escape to close
 *   - focus return to toggle on close
 *   - manual locale switch persisted to cached user.preferences.language
 *     so the auth-locale-redirect (#1116/#1121) does not bounce the
 *     navigation back to the previous locale on the very next page load
 *
 * Kept free of Shadow DOM so global blueprint tokens and the existing
 * .bp-lang-* styles apply unchanged.
 */

const USER_KEY = 'smartpluvia-user';
const LOCALE_PATTERN = /^[a-z]{2}$/;

export class BpLangDropdown extends HTMLElement {
  private toggle: HTMLButtonElement | null = null;
  private menu: HTMLElement | null = null;

  connectedCallback() {
    this.toggle = this.querySelector('.bp-lang-btn');
    this.menu = this.querySelector('.bp-lang-menu');
    if (!this.toggle || !this.menu) return;

    this.toggle.addEventListener('click', this.onToggleClick);
    this.menu.addEventListener('click', this.onCellClick);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeyDown);
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.onToggleClick);
    this.menu?.removeEventListener('click', this.onCellClick);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeyDown);
  }

  private onToggleClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.setOpen(!this.isOpen);
  };

  /**
   * When the user picks a locale from the grid, we let the link's default
   * navigation happen (so we don't fight the browser's history handling),
   * but first we update the cached user blob so `enforcePreferredLocale`
   * on the destination page sees the new locale and skips its redirect.
   *
   * Without this, an authenticated user with `preferences.language = "en"`
   * who clicks UK in the dropdown ends up bounced back to /en/... on the
   * next paint — the manual switch silently undone.
   */
  private onCellClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const cell = target.closest<HTMLAnchorElement>('a.bp-lang-cell[href]');
    if (!cell) return;
    const href = cell.getAttribute('href') || '';
    const match = href.match(/^\/([a-z]{2})(?:\/|$)/);
    if (!match || !LOCALE_PATTERN.test(match[1])) return;
    persistCachedUserLocale(match[1]);
    // Default <a> navigation continues — no preventDefault.
  };

  private onDocumentClick = (event: MouseEvent) => {
    if (!this.isOpen) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.setOpen(false);
  };

  private onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      this.setOpen(false);
      this.toggle?.focus();
    }
  };

  private get isOpen() {
    return this.toggle?.getAttribute('aria-expanded') === 'true';
  }

  private setOpen(open: boolean) {
    if (!this.toggle || !this.menu) return;
    this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) {
      this.menu.removeAttribute('hidden');
    } else {
      this.menu.setAttribute('hidden', '');
    }
  }
}

/**
 * Native-select counterpart rendered inside the mobile hamburger menu.
 * It shares the same locale persistence behavior as the desktop grid while
 * keeping the canonical 20-link dropdown unique in the document.
 */
export class BpLangSelect extends HTMLElement {
  private select: HTMLSelectElement | null = null;
  navigate: (href: string) => void = (href) => window.location.assign(href);

  connectedCallback() {
    this.select = this.querySelector('.bp-nav-mobile-lang-select');
    this.select?.addEventListener('change', this.onChange);
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this.onChange);
  }

  private onChange = () => {
    const option = this.select?.selectedOptions[0];
    const locale = option?.dataset.locale ?? '';
    const href = option?.value ?? '';
    if (!LOCALE_PATTERN.test(locale) || !href.match(new RegExp(`^/${locale}(?:/|$)`))) return;

    persistCachedUserLocale(locale);
    this.navigate(href);
  };
}

/**
 * Patch the cached user object in localStorage so its preferred-locale
 * matches the locale the user just clicked on. No-op for guests (no
 * cached user) — guests don't have a redirect to fight in the first place.
 *
 * Exported for unit tests in app/frontend/tests/.
 */
export function persistCachedUserLocale(locale: string): void {
  let raw: string | null;
  try {
    raw = localStorage.getItem(USER_KEY);
  } catch {
    return;
  }
  if (!raw) return;

  try {
    const user = JSON.parse(raw);
    if (!user || typeof user !== 'object') return;
    user.preferences = user.preferences && typeof user.preferences === 'object' ? user.preferences : {};
    user.preferences.language = locale;
    user.locale = locale;
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    // Cached payload is malformed; safer to leave it alone than overwrite.
  }
}

if (!customElements.get('bp-lang-dropdown')) {
  customElements.define('bp-lang-dropdown', BpLangDropdown);
}

if (!customElements.get('bp-lang-select')) {
  customElements.define('bp-lang-select', BpLangSelect);
}
